// SchedeCFBox.jsx
// Gestione schede di sicurezza C&F - lista, aggiungi, modifica, elimina
import React, { useState, useEffect } from "react";

const theme = {
  bg: "#0b0d12",
  cardBg: "#13161e",
  surface: "#181b25",
  border: "#2a2e45",
  text: "#e8eaf2",
  textMuted: "#9aa0b3",
  textDim: "#6b6f80",
  accent: "#f5a623",
};

function getJWT() {
  return (typeof window !== "undefined" && window.localStorage)
    ? window.localStorage.getItem("bertoc_jwt") || ""
    : "";
}

async function apiGetSchede() {
  const r = await fetch("/api/schede", { headers: { "Authorization": "Bearer " + getJWT() }});
  if (!r.ok) throw new Error("Errore lettura schede: " + r.status);
  return r.json();
}

async function apiPostScheda(data) {
  const r = await fetch("/api/schede", {
    method: "POST",
    headers: { "Authorization": "Bearer " + getJWT(), "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  const j = await r.json();
  if (!r.ok) throw new Error(j.error || "Errore upload");
  return j;
}

async function apiPutScheda(codice, data) {
  const r = await fetch("/api/schede/" + encodeURIComponent(codice), {
    method: "PUT",
    headers: { "Authorization": "Bearer " + getJWT(), "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  const j = await r.json();
  if (!r.ok) throw new Error(j.error || "Errore modifica");
  return j;
}

async function apiDeleteScheda(codice) {
  const r = await fetch("/api/schede/" + encodeURIComponent(codice), {
    method: "DELETE",
    headers: { "Authorization": "Bearer " + getJWT() },
  });
  const j = await r.json();
  if (!r.ok) throw new Error(j.error || "Errore elimina");
  return j;
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(fr.result);
    fr.onerror = () => reject(new Error("Errore lettura file"));
    fr.readAsDataURL(file);
  });
}

export default function SchedeCFBox() {
  const [schede, setSchede] = useState([]);
  const [macroCategorie, setMacroCategorie] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filtro, setFiltro] = useState("");
  const [filtroMacro, setFiltroMacro] = useState("");
  const [showFormNuova, setShowFormNuova] = useState(false);
  const [editingCodice, setEditingCodice] = useState(null);
  const [macroAperte, setMacroAperte] = useState({});

  async function ricarica() {
    try {
      setLoading(true);
      const j = await apiGetSchede();
      setSchede(j.schede || []);
      setMacroCategorie(j.macro_categorie || {});
      setError("");
    } catch(e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { ricarica(); }, []);

  // Raggruppo per macro_codice
  const schedeFiltrate = schede.filter(s => {
    if (filtroMacro && s.macro_codice !== filtroMacro) return false;
    if (filtro) {
      const q = filtro.toLowerCase();
      return (s.codice || "").toLowerCase().includes(q) ||
             (s.nome || "").toLowerCase().includes(q);
    }
    return true;
  });

  const perMacro = {};
  for (const s of schedeFiltrate) {
    const k = s.macro_codice || "??";
    if (!perMacro[k]) perMacro[k] = { codice: k, nome: s.macro_nome || macroCategorie[k] || k, schede: [] };
    perMacro[k].schede.push(s);
  }
  const gruppi = Object.values(perMacro).sort((a,b) => a.codice.localeCompare(b.codice));

  function toggleMacro(codice) {
    setMacroAperte(prev => ({ ...prev, [codice]: !prev[codice] }));
  }

  async function handleVisualizza(codice) {
    const url = "/api/schede/" + encodeURIComponent(codice) + "/file";
    const token = getJWT();
    try {
      const w = window.open("", "_blank"); /* PASS_FILE_v1: pass breve, valido 5 minuti solo per questo file */
      try {
        const rp = await fetch("/api/pass-file", { method: "POST", headers: { "Content-Type": "application/json", "Authorization": "Bearer " + (token || "") }, body: JSON.stringify({ url: url }) });
        const jp = await rp.json();
        if (!rp.ok) throw new Error(jp.error || ("HTTP " + rp.status));
        if (w) { w.opener = null; w.location.href = jp.url; } else { window.location.href = jp.url; }
      } catch (e2) { if (w) w.close(); throw e2; }
    } catch(e) {
      alert("❌ " + e.message);
    }
  }

  async function handleElimina(scheda) {
    if (!window.confirm(`Eliminare la scheda ${scheda.codice} - ${scheda.nome}?\n\nIl PDF verrà spostato nel cestino.`)) return;
    try {
      await apiDeleteScheda(scheda.codice);
      await ricarica();
      alert("✅ Scheda eliminata.");
    } catch(e) {
      alert("❌ " + e.message);
    }
  }

  return (
    <div>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:16, flexWrap:"wrap", gap:10 }}>
        <div>
          <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:18 }}>
            📋 Schede di Sicurezza C&F
          </div>
          <div style={{ fontSize:12, color:theme.textMuted, marginTop:2 }}>
            {schede.length} schede totali · raggruppate per macro categoria
          </div>
        </div>
        <button
          onClick={()=>setShowFormNuova(true)}
          className="btn btn-primary"
          style={{ background:theme.accent, color:"#000", border:"none", padding:"8px 14px", borderRadius:8, fontWeight:700, cursor:"pointer" }}
        >+ Aggiungi scheda</button>
      </div>

      <div style={{ display:"flex", gap:10, marginBottom:14, flexWrap:"wrap" }}>
        <input
          type="text"
          placeholder="🔍 Cerca per codice o nome..."
          value={filtro}
          onChange={e=>setFiltro(e.target.value)}
          style={{ flex:1, minWidth:200, background:theme.bg, border:`1px solid ${theme.border}`, borderRadius:8, color:theme.text, padding:"8px 12px", fontSize:13 }}
        />
        <select
          value={filtroMacro}
          onChange={e=>setFiltroMacro(e.target.value)}
          style={{ background:theme.bg, border:`1px solid ${theme.border}`, borderRadius:8, color:theme.text, padding:"8px 12px", fontSize:13, minWidth:150 }}
        >
          <option value="">Tutte le macro</option>
          {Object.entries(macroCategorie).sort().map(([k, n]) => (
            <option key={k} value={k}>{k} - {n}</option>
          ))}
        </select>
      </div>

      {loading && <div style={{ padding:24, textAlign:"center", color:theme.textMuted }}>Caricamento...</div>}
      {error && <div style={{ padding:14, background:"#3f0f0f", border:"1px solid #f06060", borderRadius:8, color:"#ff8080", marginBottom:10 }}>{error}</div>}

      {!loading && gruppi.length === 0 && (
        <div style={{ padding:30, textAlign:"center", color:theme.textDim, background:theme.surface, borderRadius:10 }}>
          Nessuna scheda trovata.
        </div>
      )}

      {!loading && gruppi.map(g => (
        <div key={g.codice} style={{ marginBottom:12, background:theme.surface, borderRadius:10, border:`1px solid ${theme.border}`, overflow:"hidden" }}>
          <div
            onClick={()=>toggleMacro(g.codice)}
            style={{ padding:"12px 16px", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"space-between", borderBottom: macroAperte[g.codice] ? `1px solid ${theme.border}` : "none", userSelect:"none" }}
          >
            <div>
              <span style={{ fontFamily:"'Syne',sans-serif", fontWeight:700, fontSize:15, color:theme.accent }}>{g.codice}</span>
              <span style={{ marginLeft:10, fontSize:14 }}>{g.nome}</span>
            </div>
            <div style={{ display:"flex", alignItems:"center", gap:10 }}>
              <span style={{ background:theme.bg, padding:"3px 10px", borderRadius:20, fontSize:12, fontWeight:600 }}>
                {g.schede.length} schede
              </span>
              <span style={{ fontSize:14, color:theme.textMuted }}>{macroAperte[g.codice] ? "▲" : "▼"}</span>
            </div>
          </div>
          {macroAperte[g.codice] && (
            <div>
              {g.schede.map(s => (
                <div key={s.codice} style={{ padding:"10px 16px", borderBottom:`1px solid ${theme.border}33`, display:"flex", alignItems:"center", justifyContent:"space-between", gap:12, flexWrap:"wrap" }}>
                  <div style={{ flex:1, minWidth:200 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                      <span style={{ fontFamily:"'Syne',sans-serif", fontWeight:700, color:theme.accent, fontSize:13 }}>{s.codice}</span>
                      {!s.fileEsiste && <span style={{ background:"#3f0f0f", color:"#ff8080", padding:"1px 8px", borderRadius:4, fontSize:10, fontWeight:700 }}>FILE MANCANTE</span>}
                      <span style={{ fontSize:11, color:theme.textDim }}>· {s.pagine || "?"} pag · {s.dimensione ? Math.round(s.dimensione/1024)+"kb" : ""}</span>
                    </div>
                    <div style={{ fontSize:13, color:theme.text, marginTop:3 }}>{s.nome}</div>
                  </div>
                  <div style={{ display:"flex", gap:6 }}>
                    {s.fileEsiste && (
                      <button onClick={()=>handleVisualizza(s.codice)} title="Visualizza PDF" style={{ background:"#1e3a8a44", border:"1px solid #4e9af5", color:"#4e9af5", padding:"5px 10px", borderRadius:6, cursor:"pointer", fontSize:13 }}>👁️</button>
                    )}
                    <button onClick={()=>setEditingCodice(s.codice)} title="Modifica metadata" style={{ background:"#3f350044", border:"1px solid #f5a623", color:"#f5a623", padding:"5px 10px", borderRadius:6, cursor:"pointer", fontSize:13 }}>✏️</button>
                    <button onClick={()=>handleElimina(s)} title="Elimina scheda" style={{ background:"#3f0f0f44", border:"1px solid #f06060", color:"#f06060", padding:"5px 10px", borderRadius:6, cursor:"pointer", fontSize:13 }}>🗑️</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}

      {showFormNuova && (
        <FormScheda
          macroCategorie={macroCategorie}
          onClose={()=>setShowFormNuova(false)}
          onSaved={async ()=>{ setShowFormNuova(false); await ricarica(); }}
        />
      )}
      {editingCodice && (
        <FormSchedaEdit
          codice={editingCodice}
          schede={schede}
          macroCategorie={macroCategorie}
          onClose={()=>setEditingCodice(null)}
          onSaved={async ()=>{ setEditingCodice(null); await ricarica(); }}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Form modale per AGGIUNGERE una nuova scheda
// ─────────────────────────────────────────────────────────────────────
function FormScheda({ macroCategorie, onClose, onSaved }) {
  const [codice, setCodice] = useState("");
  const [nome, setNome] = useState("");
  const [macroCodice, setMacroCodice] = useState("");
  const [macroNome, setMacroNome] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  async function handleSalva() {
    setErr("");
    if (!codice || !/^[A-Z]{2}\d{3}$/.test(codice.toUpperCase())) { setErr("Codice non valido (formato XX###, es. ED160)"); return; }
    if (!nome.trim()) { setErr("Nome scheda obbligatorio"); return; }
    if (!file) { setErr("Carica il PDF"); return; }

    try {
      setBusy(true);
      const b64 = await fileToBase64(file);
      const macroFinale = (macroCodice || codice.substring(0,2)).toUpperCase();
      const macroNomeFinale = macroNome || macroCategorie[macroFinale] || "";
      await apiPostScheda({
        codice: codice.toUpperCase(),
        nome: nome.trim(),
        macro_codice: macroFinale,
        macro_nome: macroNomeFinale,
        categoria_macro: macroNomeFinale.toUpperCase(),
        pagine: 0, // backend lo lascia 0, l'utente puo' modificarlo dopo
        pdfBase64: b64,
      });
      alert("✅ Scheda " + codice.toUpperCase() + " aggiunta.");
      onSaved();
    } catch(e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }

  // Quando cambia codice, auto-suggerisco macro_codice
  function handleCodiceChange(v) {
    const cu = v.toUpperCase();
    setCodice(cu);
    if (cu.length >= 2 && !macroCodice) {
      const mc = cu.substring(0,2);
      setMacroCodice(mc);
      if (macroCategorie[mc] && !macroNome) setMacroNome(macroCategorie[mc]);
    }
  }

  return (
    <div onClick={onClose} style={{
      position:"fixed", top:0, left:0, right:0, bottom:0, background:"rgba(0,0,0,0.7)",
      zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center", padding:20,
    }}>
      <div onClick={e=>e.stopPropagation()} style={{
        background:theme.cardBg, borderRadius:12, padding:24, maxWidth:600, width:"100%",
        border:`1px solid ${theme.border}`,
      }}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:20 }}>
          <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:18 }}>+ Nuova scheda C&F</div>
          <button onClick={onClose} style={{ background:"transparent", border:"none", color:theme.text, fontSize:22, cursor:"pointer" }}>✕</button>
        </div>

        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:14 }}>
          <div>
            <div style={{ fontSize:11, color:theme.textMuted, marginBottom:4, textTransform:"uppercase" }}>Codice (XX###)</div>
            <input type="text" value={codice} onChange={e=>handleCodiceChange(e.target.value)} placeholder="ED160" maxLength={5} style={{ width:"100%", background:theme.bg, border:`1px solid ${theme.border}`, borderRadius:8, color:theme.text, padding:"8px 12px", fontSize:14, textTransform:"uppercase" }} />
          </div>
          <div>
            <div style={{ fontSize:11, color:theme.textMuted, marginBottom:4, textTransform:"uppercase" }}>Macro categoria</div>
            <input type="text" value={macroCodice} onChange={e=>setMacroCodice(e.target.value.toUpperCase())} placeholder="ED" maxLength={2} style={{ width:"100%", background:theme.bg, border:`1px solid ${theme.border}`, borderRadius:8, color:theme.text, padding:"8px 12px", fontSize:14, textTransform:"uppercase" }} />
          </div>
        </div>

        <div style={{ marginBottom:14 }}>
          <div style={{ fontSize:11, color:theme.textMuted, marginBottom:4, textTransform:"uppercase" }}>Nome scheda</div>
          <input type="text" value={nome} onChange={e=>setNome(e.target.value)} placeholder="es. Tinteggiatura pareti e soffitti" style={{ width:"100%", background:theme.bg, border:`1px solid ${theme.border}`, borderRadius:8, color:theme.text, padding:"8px 12px", fontSize:14 }} />
        </div>

        <div style={{ marginBottom:14 }}>
          <div style={{ fontSize:11, color:theme.textMuted, marginBottom:4, textTransform:"uppercase" }}>Nome macro categoria</div>
          <input type="text" value={macroNome} onChange={e=>setMacroNome(e.target.value)} placeholder="es. Opere Edili" style={{ width:"100%", background:theme.bg, border:`1px solid ${theme.border}`, borderRadius:8, color:theme.text, padding:"8px 12px", fontSize:14 }} />
        </div>

        <div style={{ marginBottom:18 }}>
          <div style={{ fontSize:11, color:theme.textMuted, marginBottom:4, textTransform:"uppercase" }}>File PDF</div>
          <input type="file" accept="application/pdf,.pdf" onChange={e=>setFile(e.target.files[0])} style={{ width:"100%", color:theme.text, fontSize:13 }} />
          {file && <div style={{ fontSize:12, color:theme.accent, marginTop:6 }}>📎 {file.name} ({Math.round(file.size/1024)}kb)</div>}
        </div>

        {err && <div style={{ padding:10, background:"#3f0f0f", border:"1px solid #f06060", borderRadius:8, color:"#ff8080", marginBottom:14, fontSize:13 }}>{err}</div>}

        <div style={{ display:"flex", gap:10, justifyContent:"flex-end" }}>
          <button onClick={onClose} disabled={busy} style={{ background:"transparent", border:`1px solid ${theme.border}`, color:theme.text, padding:"8px 16px", borderRadius:8, cursor:"pointer" }}>Annulla</button>
          <button onClick={handleSalva} disabled={busy} style={{ background:theme.accent, color:"#000", border:"none", padding:"8px 16px", borderRadius:8, fontWeight:700, cursor: busy?"wait":"pointer", opacity: busy?0.6:1 }}>{busy?"Caricamento...":"💾 Salva"}</button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Form modale per MODIFICARE metadata di una scheda esistente
// ─────────────────────────────────────────────────────────────────────
function FormSchedaEdit({ codice, schede, macroCategorie, onClose, onSaved }) {
  const cur = schede.find(s => s.codice === codice) || {};
  const [nome, setNome] = useState(cur.nome || "");
  const [macroCodice, setMacroCodice] = useState(cur.macro_codice || "");
  const [macroNome, setMacroNome] = useState(cur.macro_nome || "");
  const [pagine, setPagine] = useState(cur.pagine || 0);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  async function handleSalva() {
    setErr("");
    if (!nome.trim()) { setErr("Nome obbligatorio"); return; }
    try {
      setBusy(true);
      await apiPutScheda(codice, {
        nome: nome.trim(),
        macro_codice: macroCodice.toUpperCase(),
        macro_nome: macroNome,
        categoria_macro: macroNome.toUpperCase(),
        pagine: parseInt(pagine) || 0,
      });
      alert("✅ Scheda modificata.");
      onSaved();
    } catch(e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div onClick={onClose} style={{ position:"fixed", top:0, left:0, right:0, bottom:0, background:"rgba(0,0,0,0.7)", zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}>
      <div onClick={e=>e.stopPropagation()} style={{ background:theme.cardBg, borderRadius:12, padding:24, maxWidth:600, width:"100%", border:`1px solid ${theme.border}` }}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:20 }}>
          <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:18 }}>✏️ Modifica scheda <span style={{ color:theme.accent }}>{codice}</span></div>
          <button onClick={onClose} style={{ background:"transparent", border:"none", color:theme.text, fontSize:22, cursor:"pointer" }}>✕</button>
        </div>

        <div style={{ marginBottom:14 }}>
          <div style={{ fontSize:11, color:theme.textMuted, marginBottom:4, textTransform:"uppercase" }}>Nome scheda</div>
          <input type="text" value={nome} onChange={e=>setNome(e.target.value)} style={{ width:"100%", background:theme.bg, border:`1px solid ${theme.border}`, borderRadius:8, color:theme.text, padding:"8px 12px", fontSize:14 }} />
        </div>

        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:12, marginBottom:14 }}>
          <div>
            <div style={{ fontSize:11, color:theme.textMuted, marginBottom:4, textTransform:"uppercase" }}>Macro</div>
            <input type="text" value={macroCodice} onChange={e=>setMacroCodice(e.target.value.toUpperCase())} maxLength={2} style={{ width:"100%", background:theme.bg, border:`1px solid ${theme.border}`, borderRadius:8, color:theme.text, padding:"8px 12px", fontSize:14, textTransform:"uppercase" }} />
          </div>
          <div>
            <div style={{ fontSize:11, color:theme.textMuted, marginBottom:4, textTransform:"uppercase" }}>Nome macro</div>
            <input type="text" value={macroNome} onChange={e=>setMacroNome(e.target.value)} style={{ width:"100%", background:theme.bg, border:`1px solid ${theme.border}`, borderRadius:8, color:theme.text, padding:"8px 12px", fontSize:14 }} />
          </div>
          <div>
            <div style={{ fontSize:11, color:theme.textMuted, marginBottom:4, textTransform:"uppercase" }}>Pagine</div>
            <input type="number" value={pagine} onChange={e=>setPagine(e.target.value)} style={{ width:"100%", background:theme.bg, border:`1px solid ${theme.border}`, borderRadius:8, color:theme.text, padding:"8px 12px", fontSize:14 }} />
          </div>
        </div>

        {err && <div style={{ padding:10, background:"#3f0f0f", border:"1px solid #f06060", borderRadius:8, color:"#ff8080", marginBottom:14, fontSize:13 }}>{err}</div>}

        <div style={{ display:"flex", gap:10, justifyContent:"flex-end" }}>
          <button onClick={onClose} disabled={busy} style={{ background:"transparent", border:`1px solid ${theme.border}`, color:theme.text, padding:"8px 16px", borderRadius:8, cursor:"pointer" }}>Annulla</button>
          <button onClick={handleSalva} disabled={busy} style={{ background:theme.accent, color:"#000", border:"none", padding:"8px 16px", borderRadius:8, fontWeight:700, cursor: busy?"wait":"pointer", opacity: busy?0.6:1 }}>{busy?"Salvataggio...":"💾 Salva"}</button>
        </div>
      </div>
    </div>
  );
}
